import { cn } from '@shadcn/lib/utils';
import {
  DropdownMenu as ShadcnDropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shadcn/ui/dropdown-menu';
import { Menu } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

type BaseMenuItem = {
  readonly disabled?: boolean;
  readonly id: string;
  readonly label: string;
};

type ActionMenuItem = BaseMenuItem & {
  readonly onClick: () => void;
};

type LinkMenuItem = BaseMenuItem & {
  readonly href: string;
};

type MenuItem = ActionMenuItem | LinkMenuItem;

type DropdownMenuProps = {
  readonly align?: 'start' | 'center' | 'end';
  readonly children?: React.ReactNode;
  readonly className?: string;
  readonly contentClassName?: string;
  readonly disabled?: boolean;
  readonly emptyMessage?: string;
  readonly items?: readonly MenuItem[];
  readonly onNavigate?: (href: string) => void;
  readonly sideOffset?: number;
  readonly 'aria-label'?: string;
  readonly 'data-testid'?: string;
};

const defaultStyles = {
  content: 'min-w-[160px] max-w-[280px] sm:min-w-[200px] bg-background border border-border shadow-lg rounded-lg p-1',
  defaultTrigger:
    'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-9 w-9',
  empty:
    'text-muted-foreground cursor-default hover:bg-transparent focus:bg-transparent py-3 px-3 text-sm font-sans h-12 flex items-center',
  item: 'cursor-pointer py-2.5 px-3 text-sm text-foreground hover:bg-accent focus:bg-accent rounded-md transition-colors font-sans',
  itemDisabled: 'text-muted-foreground cursor-not-allowed hover:bg-transparent focus:bg-transparent',
};

const TAB_INDEX = {
  DISABLED: -1,
  ENABLED: 0,
} as const;

function useMenuItemHandler(onNavigate?: (href: string) => void) {
  return React.useCallback(
    (item: MenuItem) => {
      if (item.disabled) return;

      if ('onClick' in item) {
        item.onClick();
      } else if ('href' in item && onNavigate) {
        onNavigate(item.href);
      }
    },
    [onNavigate]
  );
}

function DefaultMenuTrigger({ ariaLabel, testId }: { readonly ariaLabel?: string; readonly testId?: string }) {
  const { t } = useTranslation();

  return (
    <span className={defaultStyles.defaultTrigger} data-testid={testId}>
      <Menu className="h-4 w-4" />
      <span className="sr-only">{ariaLabel ?? t('dropdownMenu.openMenu', 'Open menu')}</span>
    </span>
  );
}

function MenuItemComponent({
  menuItem,
  onItemClick,
}: {
  readonly menuItem: MenuItem;
  readonly onItemClick: (item: MenuItem) => void;
}) {
  function getItemClassName(item: MenuItem) {
    return cn(defaultStyles.item, item.disabled && defaultStyles.itemDisabled);
  }

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if ((event.key === 'Enter' || event.key === ' ') && !menuItem.disabled) {
        event.preventDefault();
        onItemClick(menuItem);
      }
    },
    [menuItem, onItemClick]
  );

  return (
    <DropdownMenuItem
      className={getItemClassName(menuItem)}
      disabled={menuItem.disabled}
      onClick={() => onItemClick(menuItem)}
      onKeyDown={handleKeyDown}
      role="menuitem"
      tabIndex={menuItem.disabled ? TAB_INDEX.DISABLED : TAB_INDEX.ENABLED}
    >
      <span className="truncate">{menuItem.label}</span>
    </DropdownMenuItem>
  );
}

function MenuContentComponent({
  align,
  contentClassName,
  emptyMessage,
  handleMenuItemClick,
  items,
  sideOffset,
}: {
  readonly align: 'start' | 'center' | 'end';
  readonly contentClassName?: string;
  readonly emptyMessage: string;
  readonly handleMenuItemClick: (item: MenuItem) => void;
  readonly items?: readonly MenuItem[];
  readonly sideOffset?: number;
}) {
  const { t } = useTranslation();

  return (
    <DropdownMenuContent
      align={align}
      aria-orientation="vertical"
      className={cn(defaultStyles.content, contentClassName)}
      role="menu"
      sideOffset={sideOffset}
    >
      {!items?.length ? (
        <output className={cn(defaultStyles.item, defaultStyles.empty)}>
          {emptyMessage || t('dropdownMenu.noItemsAvailable', 'No items available')}
        </output>
      ) : (
        items.map((listItem) => (
          <MenuItemComponent key={listItem.id} menuItem={listItem} onItemClick={handleMenuItemClick} />
        ))
      )}
    </DropdownMenuContent>
  );
}

function DropdownMenu({
  align = 'end',
  children,
  className,
  contentClassName,
  disabled,
  emptyMessage,
  items,
  onNavigate,
  sideOffset,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: DropdownMenuProps): React.JSX.Element {
  const { t } = useTranslation();
  const handleMenuItemClick = useMenuItemHandler(onNavigate);

  const translatedEmptyMessage = emptyMessage ?? t('dropdownMenu.noItemsAvailable', 'No items available');

  const triggerContent = children ?? <DefaultMenuTrigger ariaLabel={ariaLabel} testId={testId} />;

  return (
    <ShadcnDropdownMenu>
      <DropdownMenuTrigger
        aria-haspopup="menu"
        aria-label={ariaLabel}
        asChild={Boolean(children)}
        className={children ? undefined : className}
        data-testid={testId}
        disabled={disabled}
      >
        {triggerContent}
      </DropdownMenuTrigger>
      <MenuContentComponent
        align={align}
        contentClassName={contentClassName}
        emptyMessage={translatedEmptyMessage}
        handleMenuItemClick={handleMenuItemClick}
        items={items}
        sideOffset={sideOffset}
      />
    </ShadcnDropdownMenu>
  );
}

DropdownMenu.displayName = 'DropdownMenu';

export type { ActionMenuItem, DropdownMenuProps, LinkMenuItem, MenuItem };
export { DropdownMenu };
